// /optimize — cross-portfolio keyword target list. Every (site, keyword)
// pair where we rank 11-50, sorted by computed ease score (lower = easier
// to push to top-10). This is the operator's weekly planning surface:
// pick the top N targets, work them, re-probe in 2 weeks.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { optimizeApi, type OptimizeTarget } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

type PositionFilter = "all" | "11-30" | "31-50";

export function OptimizePage() {
  const [posFilter, setPosFilter] = useState<PositionFilter>("11-30");
  const [siteFilter, setSiteFilter] = useState("");
  const q = useQuery({
    queryKey: ["optimize-targets"],
    queryFn: optimizeApi.targets,
    refetchInterval: 60_000,
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Optimize</h1>
          <p className="text-ink-600 text-sm mt-1">
            Cross-portfolio target list — keywords ranking 11-50 across all sites, sorted by ease of pushing to top-10. Lower ease score = closer to page 1 with less competition.{" "}
            <Link to="/agents/spencer-spy" className="text-accent hover:underline">Spencer</Link>{" updates this weekly."}
          </p>
        </div>
      </div>

      {q.isLoading && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>
      )}
      {q.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          Failed: {String((q.error as Error).message)}
        </div>
      )}
      {q.data && (
        <Body
          data={q.data}
          posFilter={posFilter}
          setPosFilter={setPosFilter}
          siteFilter={siteFilter}
          setSiteFilter={setSiteFilter}
        />
      )}
    </div>
  );
}

function Body({
  data,
  posFilter,
  setPosFilter,
  siteFilter,
  setSiteFilter,
}: {
  data: { summary: { total: number; in1130: number; in3150: number; sites: number }; targets: OptimizeTarget[] };
  posFilter: PositionFilter;
  setPosFilter: (f: PositionFilter) => void;
  siteFilter: string;
  setSiteFilter: (s: string) => void;
}) {
  const filtered = data.targets.filter((t) => {
    if (posFilter === "11-30" && t.ourPosition > 30) return false;
    if (posFilter === "31-50" && t.ourPosition <= 30) return false;
    if (siteFilter && !t.domain.toLowerCase().includes(siteFilter.toLowerCase())) return false;
    return true;
  });

  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<OptimizeTarget>(
    filtered,
    {
      pos: (t) => t.ourPosition,
      site: (t) => t.domain,
      keyword: (t) => t.keyword,
      ease: (t) => t.ease,
      // SERP mix sorts on local slots — that's the most actionable signal.
      serpMix: (t) => t.localSlots,
      probed: (t) => t.probedAt,
    },
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
        <h2 className="font-semibold mb-3">Summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat label="Total targets" value={data.summary.total} />
          <Stat label="In 11-30 (page 2)" value={data.summary.in1130} positive={data.summary.in1130 > 0} hint="Realistic to push to top-10" />
          <Stat label="In 31-50 (page 3-5)" value={data.summary.in3150} hint="Longer climb" />
          <Stat label="Sites covered" value={`${data.summary.sites} / 31`} />
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-ink-100 p-4 shadow-sm flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {(["11-30", "31-50", "all"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPosFilter(p)}
              className={
                "px-2.5 py-1 rounded-md text-xs font-medium transition-colors " +
                (posFilter === p ? "bg-ink-900 text-white" : "bg-ink-50 text-ink-600 hover:bg-ink-100")
              }
            >
              {p === "all" ? "All 11-50" : p}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Filter by domain…"
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="text-sm px-3 py-1 border border-ink-200 rounded-md w-64"
        />
        <span className="text-xs text-ink-500 ml-auto">
          Showing {filtered.length} of {data.targets.length}
        </span>
      </div>

      {/* Target table */}
      <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <SortHeader label="Pos" sortKey="pos" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-3 py-3 w-14" />
              <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-3 py-3" />
              <SortHeader label="Keyword" sortKey="keyword" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-3 py-3" />
              <SortHeader label="Ease" sortKey="ease" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-3 py-3" title="Ease score: lower = easier. distance + 0.5×dir + 1×chain + 1.5×local" />
              <SortHeader label="SERP mix" sortKey="serpMix" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-3 py-3" title="Sorted by # of local-pack slots (the dominant signal)." />
              <SortHeader label="Probed" sortKey="probed" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center px-3 py-8 text-sm text-ink-500">
                  {data.targets.length === 0
                    ? "No targets — Spencer hasn't probed yet, or no sites are ranking 11-50."
                    : "No matches with current filter."}
                </td>
              </tr>
            ) : (
              sortedRows.map((t) => <TargetRow key={`${t.siteId}-${t.keyword}`} t={t} />)
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function TargetRow({ t }: { t: OptimizeTarget }) {
  const posColor =
    t.ourPosition <= 15 ? "text-green-700"
    : t.ourPosition <= 25 ? "text-amber-700"
    : "text-ink-700";
  return (
    <tr className="hover:bg-ink-50/50">
      <td className={`px-3 py-3 text-right tabular-nums font-semibold ${posColor}`}>#{t.ourPosition}</td>
      <td className="px-3 py-3">
        <Link to={`/sites/${t.siteId}`} className="text-accent hover:underline font-medium">
          {t.domain}
        </Link>
      </td>
      <td className="px-3 py-3 font-mono text-xs text-ink-700">"{t.keyword}"</td>
      <td className="px-3 py-3 text-right tabular-nums text-ink-600">{t.ease.toFixed(1)}</td>
      <td className="px-3 py-3 text-right text-xs">
        <span className="text-ink-400">{t.directorySlots}</span>
        <span className="mx-1 text-ink-300">/</span>
        <span className="text-amber-700">{t.chainSlots}</span>
        <span className="mx-1 text-ink-300">/</span>
        <span className="text-ink-800 font-semibold">{t.localSlots}</span>
        <div className="text-[10px] text-ink-400 mt-0.5">dir / chain / local</div>
      </td>
      <td className="px-3 py-3 text-right text-xs text-ink-500 tabular-nums whitespace-nowrap">
        {formatAgo(t.probedAt)}
      </td>
    </tr>
  );
}

function Stat({ label, value, positive, hint }: {
  label: string; value: string | number; positive?: boolean; hint?: string;
}) {
  const color = positive ? "text-green-700" : "text-ink-900";
  return (
    <div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      <div className="text-[11px] text-ink-500 uppercase tracking-wide">{label}</div>
      {hint && <div className="text-[10px] text-ink-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function formatAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
